import { ReactElement, useEffect, useState } from 'react';
import { Tooltip as RMWCTooltip } from '@rmwc/tooltip';
import { propTypes, defaultProps } from './_types';

import '@rmwc/tooltip/styles';
import './Tooltip.scss';

/**
 * Generic functional component for displaying informative text on user interaction with child component.
 * @param {Object} props JSX attributes passed to component.
 * @returns {ReactElement} rendered component.
 */
const Tooltip = ({ open = null, peek, peekInterval = 3000, ...props }) => {
	//#region Local state
	/**
	 * @typedef {boolean} sIsTemporarilyOpen local state toggling temporary "open" state of component.
	 */
	/**
	 * @typedef {Function} setIsTemporarilyOpen Setter for temporary "open" state.
	 */
	const [sIsTemporarilyOpen, setIsTemporarilyOpen] = useState();
	/**
	 * @typedef {number} sTimeoutID local state possessing nullable ID of non-expired timer.
	 */
	/**
	 *  @typedef {Function} setTimeoutID setter for non-expired timer ID.
	 */
	const [sTimeoutID, setTimeoutID] = useState();
	//#endregion

	//#region Lifecycle hooks
	/**
	 * Lifecycle hook on `props` change. Temporarily opens tooltip content while a set time period has not elapsed.
	 * ID of timeout managed in local state, timeout resets per change to @param deps list.
	 * Content will remain open if open state updated from 'true' directly to 'null', must first be updated to 'false'.
	 * @returns {Function} clean-up clearing timeout.
	 */
	useEffect(() => {
		if (sTimeoutID) {
			clearTimeout(sTimeoutID);
		}
		setIsTemporarilyOpen(peek);
		const timeoutID = setTimeout(
			() => {
				setIsTemporarilyOpen(peek ? false : null);
				setTimeoutID(
					peek
						? setTimeout(() => {
								setIsTemporarilyOpen(null);
								setTimeoutID(null);
						  }, 1)
						: null
				);
			},
			peek ? peekInterval : 1
		);
		setTimeoutID(timeoutID);

		return () => {
			clearTimeout(timeoutID);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [peek]);
	//#endregion

	return (
		<RMWCTooltip
			open={
				open !== null
					? open
					: sIsTemporarilyOpen !== null
					? sIsTemporarilyOpen
					: undefined
			}
			{...props}
		>
			{props.children}
		</RMWCTooltip>
	);
};

Tooltip.propTypes = propTypes;
Tooltip.defaultProps = defaultProps;

export { Tooltip };
